import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useClerk, useUser } from "@clerk/react";
import { LayoutDashboard, LogOut, ShieldCheck, UserRound } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useMe } from "@/context/MeContext";
import { useApi } from "@/lib/api";
import { toast } from "sonner";

export function useLogout() {
  const { signOut } = useClerk();
  const api = useApi();
  const navigate = useNavigate();
  return useCallback(async ({ reason = "user", all = false } = {}) => {
    try { await api.post(all ? "/me/logout-all" : "/me/logout", all ? {} : { reason }); } catch { /* session may already be ended */ }
    try { localStorage.clear(); sessionStorage.clear(); } catch { /* storage unavailable */ }
    await signOut();
    if (reason === "timeout") toast.info("You were logged out after a period of inactivity.");
    navigate("/", { replace: true });
  }, [api, signOut, navigate]);
}

const initials = (n) => (n || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

export function LogoutButton({ className = "", testid }) {
  const logout = useLogout();
  return (
    <Button variant="ghost" onClick={() => logout()} className={`h-11 justify-start gap-3 text-slate-700 hover:bg-rose-50 hover:text-rose-700 ${className}`} data-testid={testid}>
      <LogOut className="h-4 w-4" /> Log out
    </Button>
  );
}

export function ProfileMenu({ testid = "profile-menu" }) {
  const { user } = useUser();
  const { me } = useMe();
  const logout = useLogout();
  const name = me?.user?.name || user?.fullName || user?.primaryEmailAddress?.emailAddress?.split("@")[0];
  const email = me?.user?.email || user?.primaryEmailAddress?.emailAddress;
  const isAdmin = !!me?.user?.is_admin;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 rounded-full p-1 pr-2 transition-colors hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#222d99]" aria-label={`Account menu for ${name || "your account"}`} data-testid={`${testid}-trigger`}>
          {user?.imageUrl ? <img src={user.imageUrl} alt="" className="h-8 w-8 rounded-full object-cover" /> : <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#222d99] text-xs font-bold text-white">{initials(name)}</span>}
          <span className="hidden max-w-[10rem] truncate text-sm font-medium text-slate-800 sm:inline">{name}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64" data-testid={`${testid}-content`}>
        <DropdownMenuLabel className="font-normal">
          <p className="truncate text-sm font-semibold text-slate-900" data-testid={`${testid}-name`}>{name}</p>
          <p className="truncate text-xs text-slate-500" data-testid={`${testid}-email`}>{email}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild className="h-10 cursor-pointer"><Link to={isAdmin ? "/admin" : "/"} data-testid={`${testid}-dashboard`}>{isAdmin ? <ShieldCheck className="mr-2 h-4 w-4" /> : <LayoutDashboard className="mr-2 h-4 w-4" />}{isAdmin ? "Admin" : "My dashboard"}</Link></DropdownMenuItem>
        <DropdownMenuItem asChild className="h-10 cursor-pointer"><Link to="/account" data-testid={`${testid}-account`}><UserRound className="mr-2 h-4 w-4" /> Account and security</Link></DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => logout()} className="h-10 cursor-pointer text-rose-700 focus:bg-rose-50 focus:text-rose-800" data-testid={`${testid}-logout`}><LogOut className="mr-2 h-4 w-4" /> Log out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const EVENTS = ["mousemove", "keydown", "mousedown", "scroll", "touchstart"];
const KEY = "crowlearn-last-activity";

export function AdminIdleGuard({ minutes }) {
  const logout = useLogout();
  const [warn, setWarn] = useState(false);
  const fired = useRef(false);
  useEffect(() => {
    const mark = () => { if (!document.hidden) localStorage.setItem(KEY, String(Date.now())); };
    mark();
    EVENTS.forEach((e) => window.addEventListener(e, mark, { passive: true }));
    const t = setInterval(() => {
      const idle = (Date.now() - Number(localStorage.getItem(KEY) || Date.now())) / 1000;
      if (idle >= minutes * 60 && !fired.current) { fired.current = true; logout({ reason: "timeout" }); }
      setWarn(idle >= minutes * 60 - 60);
    }, 5000);
    return () => { clearInterval(t); EVENTS.forEach((e) => window.removeEventListener(e, mark)); };
  }, [minutes, logout]);
  return (
    <AlertDialog open={warn}>
      <AlertDialogContent data-testid="idle-warning">
        <AlertDialogHeader>
          <AlertDialogTitle>Still there?</AlertDialogTitle>
          <AlertDialogDescription>For security, you'll be logged out in about a minute because there has been no activity.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" onClick={() => logout()} data-testid="idle-logout-now">Log out now</Button>
          <AlertDialogAction onClick={() => { localStorage.setItem(KEY, String(Date.now())); setWarn(false); }} className="bg-[#222d99] hover:bg-[#1a237e]" data-testid="idle-stay">Stay signed in</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
